import { Injectable } from '@angular/core';
import {
  ClientFromToken,
  Customer,
  LoginDto,
  LoginResponse,
  SendOtpCodeResult,
  TransferData,
  User
} from '../store/types';
import { ApiService } from './api.service';
import { API } from './constants';
import { environment } from 'src/environments/environment';
import { startAuthentication, startRegistration } from '@simplewebauthn/browser';

interface RegisterBaseDto {
  password: string;
  email: string;
  customerMetadata?: Record<string, any>;
  /** Referral link code. Required by the backend only when referral-only registration is enabled. */
  referralCode?: string;
}

export interface ReferralLink {
  _id?: string;
  code: string;
  name?: string;
  creatorUserId: string;
  isActive: boolean;
  used: boolean;
  usedByCustomerId?: string;
  usedAt?: string;
  createdAt?: string;
}

export interface PersonalRegisterDto extends RegisterBaseDto {
  customerType: 'PERSONAL';
  dateOfBirth: string;
  firstName: string;
  lastName: string;
  idCard: string;
  address: string;
  phone: string;
  birthCountryId: number;
  occupationId?: number;
}

export interface BusinessRegisterDto extends RegisterBaseDto {
  customerType: 'BUSINESS';
  businessName: string;
  taxId: string;
}

export type RegisterDto = PersonalRegisterDto | BusinessRegisterDto;

@Injectable({ providedIn: 'root' })
export class AuthService extends ApiService {
	appConfig() {
		return this.get<any>(API.appConfig);
	}

	/**
	 * Funcionalidades con audiencia restringida (grabación de sesiones, KYC)
	 * resueltas para el usuario autenticado. El backend compara contra la lista
	 * de correos habilitados y solo devuelve booleanos.
	 */
	featureFlags() {
		return this.get<{ sessionRecording: boolean; kycVerification: boolean }>(API.featureFlags);
	}
  login(dto: LoginDto) {
    return this.post<LoginResponse>(API.login, { body: dto });
  }

  register(dto: RegisterDto) {
    return this.post<Customer>(API.register, { body: dto });
  }

  createReferralSource(dto: { source: string; otherDetail?: string; email: string; customerType: string }) {
    return this.post<any>(API.createReferralSource, { body: dto });
  }

  createReferralLink(name?: string) {
    return this.post<ReferralLink>(API.createReferralLink, { body: { name } });
  }

  listReferralLinks() {
    return this.get<ReferralLink[]>(API.listReferralLinks);
  }

  validateReferralLink(code: string) {
    return this.get<{ valid: boolean }>(API.validateReferralLink(code));
  }

  deleteReferralLink(id: string) {
    return this.delete<any>(API.deleteReferralLink(id));
  }

  emailIsRegistered(email: string, captchaToken?: string) {
    // captchaToken solo se envía cuando el backend exigió verificación (428) tras
    // N consultas por IP (anti-enumeración). El uso normal no lo necesita.
    // silentError: el 428 se maneja inline (widget Turnstile), sin snackbar global.
    const query = captchaToken ? `?captchaToken=${encodeURIComponent(captchaToken)}` : '';
    // encodeURIComponent en el email: caracteres como `+`, `%`, `#` romperían el path.
    return this.get<boolean>(`${API.emailIsRegistered}/${encodeURIComponent(email)}${query}`, {
      silentError: true
    });
  }

  sendOtpCode(reason, email?: string) {
    return this.post<SendOtpCodeResult>(API.sendOtpCode, {
      body: { reason, email }
    });
  }

  sendOtpCodeToEmail(email: string, reason: string) {
    return this.post<SendOtpCodeResult>(API.sendOtpCode, {
      body: { email, reason }
    });
  }

  sendOtpCodeFromLogin(reason, token: string) {
    return this.post<SendOtpCodeResult>(API.sendOtpCode, {
      body: { reason },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  validateOtpCodeFromLogin(code: string, reason: string, token: string) {
    return this.post<SendOtpCodeResult>(API.validateOtpCode, {
      body: { code, reason },
      headers: {
        Authorization: `Bearer ${token}`
      }
    });
  }

  validateOtp(code: string, reason: string, email?: string) {
    return this.post<SendOtpCodeResult>(API.validateOtpCode, {
      body: { code, reason, email }
    });
  }

  getSession(sessionId: string) {
    return this.get(API.getSession(sessionId));
  }

  // Migración transparente al modo cookie: manda el token legacy de localStorage en el
  // header UNA vez para obtener la cookie httpOnly de sesión (así el cliente no se desloguea
  // en el deploy). silentError: si el token es inválido/expiró, el flujo sigue a login.
  migrateSession(legacyToken: string) {
    return this.post(API.migrateSession, {
      body: {},
      headers: { Authorization: `Bearer ${legacyToken}` },
      silentError: true
    });
  }

  getClientFromToken(token) {
    // El JWT viaja SOLO en el header Authorization (nunca en la URL): el query
    // ?token= exponía el bearer en logs/analítica (ver hallazgo "JWT en URL").
    // Cuando hay token explícito lo forzamos en el header (p.ej. login biométrico,
    // donde aún no está en storage); si no, ApiService usa el de storage
    // (restauración de sesión y otros callers).
    const options = token ? { headers: { Authorization: `Bearer ${token}` } } : undefined;
    return this.get<ClientFromToken>(`${API.getMe}`, options) as any;
  }

  changePassword({ currentPassword, newPassword }) {
    return this.post(API.changePassword, { body: { currentPassword, newPassword } });
  }

  // Migración JWT→cookie (Fase 2): logout server-side para limpiar la cookie httpOnly
  // (el JS no puede borrarla). Best-effort; silentError para no mostrar snackbar de error.
  logout() {
    return this.post(API.logout, { body: {}, silentError: true });
  }

  forgotPassword({ email }) {
    return this.post(API.forgotPassword, { body: { email } });
  }

  validateResetToken({ token }) {
    return this.post(API.validateResetToken, { body: { token } });
  }

  resetPassword({ token, newPassword }) {
    return this.post(API.resetPassword, { body: { resetToken: token, newPassword } });
  }

  registerTwoFa() {
    return this.post(API.registerTwoFa);
  }

  validateTwoFaCode(code: string, isForRegister?) {
    return this.post(API.validateTwoFaCode, { body: { code, isForRegister } });
  }

  recoverySecretTwoFa(phrase: string) {
    return this.post(API.recoverySecretTwoFa, { body: { phrase } });
  }

  disableTwoFa() {
    return this.post(API.disableTwoFa);
  }

  // ───────────────────────── WebAuthn / login biométrico ─────────────────────────

  /** ¿El navegador/dispositivo soporta passkeys de plataforma (huella/Face ID)? */
  static isWebauthnSupported(): boolean {
    return typeof window !== 'undefined' && !!window.PublicKeyCredential;
  }

  /** Enrola una passkey en ESTE dispositivo para el usuario autenticado. */
  async enrollPasskey(deviceName?: string): Promise<boolean> {
    const { data: options } = await this.post<any>(API.webauthnRegistrationOptions, {
      body: { deviceName }
    });
    if (!options) return false;

    const attResp = await startRegistration({ optionsJSON: options });

    const { data } = await this.post<any>(API.webauthnRegistrationVerify, {
      body: { response: attResp, deviceName }
    });
    return !!data?.credentialID;
  }

  /** Lista las passkeys enroladas por el usuario autenticado. */
  async listPasskeys(): Promise<any[]> {
    const { data } = await this.get<any>(API.webauthnCredentials);
    return Array.isArray(data) ? data : [];
  }

  /** Elimina una passkey enrolada. */
  async deletePasskey(credentialId: string): Promise<boolean> {
    const { data } = await this.delete<any>(
      `${API.webauthnCredentials}/${encodeURIComponent(credentialId)}`
    );
    return !!data?.success;
  }

  /**
   * Autoriza UNA transferencia con huella (step-up). Pide opciones atadas a
   * monto+destino, hace la ceremonia biométrica y verifica; devuelve el
   * `validationCode` listo (`bio:<nonce>`) para enviar en la transferencia, o
   * null si no hay passkey / el usuario cancela (para caer al OTP).
   */
  async authorizeTransferWithBiometrics(
    amount: number,
    destination: string
  ): Promise<string | null> {
    try {
      const { data: optionsResult } = await this.post<any>(API.webauthnStepUpOptions, {
        body: { amount, destination }
      });
      if (!optionsResult?.options || !optionsResult?.sessionId) return null;

      const asseResp = await startAuthentication({ optionsJSON: optionsResult.options });

      const { data } = await this.post<any>(API.webauthnStepUpVerify, {
        body: { sessionId: optionsResult.sessionId, response: asseResp }
      });
      return data?.nonce ? `bio:${data.nonce}` : null;
    } catch {
      // Cancelado o sin credencial → el flujo cae al OTP.
      return null;
    }
  }

  /**
   * Login passwordless con passkey (usernameless). Devuelve la respuesta de
   * verificación ({ token, user }); el orquestador (login.component) carga
   * cliente+cuentas vía getClientFromToken y guarda la sesión.
   */
  async passkeyAuthenticate(): Promise<{ token?: string; user?: User } | null> {
    const { data: optionsResult } = await this.post<any>(API.webauthnAuthenticationOptions, {
      body: {}
    });
    if (!optionsResult?.options || !optionsResult?.sessionId) return null;

    const asseResp = await startAuthentication({ optionsJSON: optionsResult.options });

    const { data } = await this.post<any>(API.webauthnAuthenticationVerify, {
      body: { sessionId: optionsResult.sessionId, response: asseResp }
    });
    return data ?? null;
  }

  getZendeskJwt() {
    return this.get<{ token: string }>(API.getZendeskJwt);
  }

  authenticateZendesk() {
    return this.getZendeskJwt()
      .then((response) => {
        if (response && response.data?.token) {
          // Inicializa Zendesk con el token JWT
          this.initZendeskAuth(response.data?.token);
          return true;
        }
        return false;
      })
      .catch((error) => {
        console.error('Error obteniendo token Zendesk:', error);
        return false;
      });
  }

  private initZendeskAuth(token: string) {
    // Verifica si el objeto global de Zendesk Web Widget existe
    if (environment.production) {
      if (typeof window !== 'undefined' && window['zE']) {
        window['zE']('messenger', 'loginUser', function (callback) {
          callback(token);
        });
      }
    }
  }

}
